import {
  Alert,
  Checkbox,
  FormControlLabel,
  HairlineCard,
  Stack,
  TextField,
  Typography,
  FormControl,
  FormHelperText,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { EscalationStageData, FileDetail } from '@munifocus/contracts-gov';
import { StageLayout } from '../../components/StageLayout';
import { useSaveStage } from '../../lib/files-api';
import { useSubmitValidation } from '../../lib/useSubmitValidation';

/**
 * Stage 5 entry — the Escalation Type Selector (ticket E1). For the D&U workflow
 * the escalation type is pre-determined as Type 3 (Form 4: Notice to Comply); the
 * other nine hub types are out of scope for v47, so the selector simply confirms
 * Type 3 and captures the authorization record that travels with the file. The
 * Steps 5–9 sub-flow opens once the authorization is recorded.
 */
export function EscalationStage({ file }: { file: FileDetail }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const save = useSaveStage(file.id, 'escalation');
  const initial = (file.stageData?.escalation ?? {}) as EscalationStageData;

  const [authorizedBy, setAuthorizedBy] = useState(initial.authorizedBy ?? '');
  const [authorizationDate, setAuthorizationDate] = useState(
    initial.authorizationDate?.slice(0, 10) ?? '',
  );
  const [authorizationRef, setAuthorizationRef] = useState(initial.authorizationRef ?? '');
  const [confirmed, setConfirmed] = useState(Boolean(initial.escalationType));

  const v = useSubmitValidation({
    authorizedBy: authorizedBy.trim() ? null : t('common.validation.required'),
    confirmed: confirmed ? null : t('common.validation.confirm'),
  });

  const onComplete = () =>
    save.mutate(
      {
        escalationType: 'type_3',
        authorizedBy: authorizedBy.trim(),
        authorizationDate: authorizationDate || undefined,
        authorizationRef: authorizationRef.trim() || undefined,
        // The file-level stage stops at `escalation`; Steps 5–9 are tracked
        // separately, so we record the authorization without advancing.
        advance: false,
      },
      { onSuccess: () => navigate(`/files/${file.id}`) },
    );

  const record = [
    {
      label: t('gov.escalation.selectedType'),
      value: confirmed ? t('gov.escalation.type3') : null,
    },
    { label: t('gov.escalation.authorizedBy'), value: authorizedBy },
    { label: t('gov.escalation.authorizationDate'), value: authorizationDate },
    { label: t('gov.escalation.authorizationRef'), value: authorizationRef },
  ];

  return (
    <StageLayout
      file={file}
      stageId="escalation"
      record={record}
      onComplete={v.guard(onComplete)}
      saving={save.isPending}
      completeLabel={t('gov.escalation.recordAuthorization')}
    >
      {save.isError && <Alert severity="error">{t('gov.stage.saveError')}</Alert>}

      <HairlineCard sx={{ p: 2.5 }}>
        <Stack spacing={0.5}>
          <Typography variant="overline" color="text.secondary">
            {t('gov.escalation.selectedType')}
          </Typography>
          <Typography variant="subtitle1">{t('gov.escalation.type3')}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('gov.escalation.type3Basis')}
          </Typography>
        </Stack>
      </HairlineCard>

      <Typography variant="subtitle2">{t('gov.escalation.authorizationRecord')}</Typography>

      <TextField
        fullWidth
        size="small"
        required
        label={t('gov.escalation.authorizedBy')}
        {...v.field('authorizedBy')}
        value={authorizedBy}
        onChange={(e) => setAuthorizedBy(e.target.value)}
      />

      <TextField
        fullWidth
        size="small"
        type="date"
        label={t('gov.escalation.authorizationDate')}
        slotProps={{ inputLabel: { shrink: true } }}
        value={authorizationDate}
        onChange={(e) => setAuthorizationDate(e.target.value)}
      />

      <TextField
        fullWidth
        size="small"
        label={t('gov.escalation.authorizationRef')}
        value={authorizationRef}
        onChange={(e) => setAuthorizationRef(e.target.value)}
      />

      <FormControl error={Boolean(v.errors.confirmed)}>
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              slotProps={{ input: { ref: v.register('confirmed') } }}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
          }
          label={t('gov.escalation.confirm')}
        />
        {v.errors.confirmed && <FormHelperText>{v.errors.confirmed}</FormHelperText>}
      </FormControl>
    </StageLayout>
  );
}
